import { motion } from "framer-motion";
import { ParticleField } from "@/components/effects/DynamicBackgrounds";
import { SEOHead } from "@/components/seo/SEOHead";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-semibold text-[#F8FAFC] mb-4">{title}</h2>
    <div className="text-sm text-[#94A3B8] leading-relaxed space-y-3">{children}</div>
  </div>
);

export const PrivacyPage = () => {
  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title="Privacy Policy"
        description="ProcureAide's privacy policy. Learn how we collect, use and protect your data."
        path="/privacy"
        noindex
      />
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-20" />
        <ParticleField count={12} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4">Legal</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] tracking-tight mb-4">
              Privacy Policy
            </h1>
            <p className="text-[#64748B] text-sm mb-12">
              Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="bg-[#0B1426]/60 border border-[#00B4D8]/10 rounded-2xl p-8 sm:p-12"
            data-testid="privacy-content"
          >
            <Section title="1. Introduction">
              <p>Procureaide Pvt Ltd ("Procureaide", "we", "us", or "our") is committed to protecting your personal data and respecting your privacy. This Privacy Policy explains how we collect, use, store, and protect your personal information when you visit our website at procureaide.co ("Platform") and use our services.</p>
              <p>This policy is designed to comply with the General Data Protection Regulation (GDPR) (EU) 2016/679, the UK GDPR, and other applicable data protection laws. Regardless of where you are located, we apply the same high standards of data protection to all users.</p>
            </Section>

            <Section title="2. Data Controller">
              <p>The data controller responsible for your personal data is:</p>
              <ul className="list-none space-y-1">
                <li><strong className="text-[#F8FAFC]">Procureaide Pvt Ltd</strong></li>
                <li>Registered in Pakistan</li>
                <li>Email: <a href="mailto:info@procureaide.com" className="text-[#00B4D8] hover:underline">info@procureaide.com</a></li>
              </ul>
              <p className="mt-3">For all privacy-related inquiries, data access requests, or complaints, please contact us at the email address above.</p>
            </Section>

            <Section title="3. Information We Collect">
              <p>We collect the following categories of personal data:</p>

              <p className="mt-4"><strong className="text-[#F8FAFC]">3.1 Information You Provide Directly</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-[#E2E8F0]">Demo Request Forms:</strong> Name, email address, company name, job title, phone number, company size, product interest, and any message you include.</li>
                <li><strong className="text-[#E2E8F0]">Newsletter Subscriptions:</strong> Email address.</li>
                <li><strong className="text-[#E2E8F0]">Contact Forms:</strong> Name, email address, company name, phone number, and message content.</li>
              </ul>

              <p className="mt-4"><strong className="text-[#F8FAFC]">3.2 Information Collected Automatically</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-[#E2E8F0]">Usage Data:</strong> Pages visited, time spent on pages, click patterns, and navigation paths.</li>
                <li><strong className="text-[#E2E8F0]">Technical Data:</strong> IP address, browser type and version, operating system, device type, and screen resolution.</li>
                <li><strong className="text-[#E2E8F0]">Cookies and Similar Technologies:</strong> See Section 8 (Cookies) for details.</li>
              </ul>
            </Section>

            <Section title="4. Legal Basis for Processing (GDPR Article 6)">
              <p>We process your personal data on the following legal bases:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-[#E2E8F0]">Consent (Article 6(1)(a)):</strong> When you subscribe to our newsletter or submit a demo request, you provide explicit consent for us to process your data for the stated purpose.</li>
                <li><strong className="text-[#E2E8F0]">Legitimate Interest (Article 6(1)(f)):</strong> We process usage and technical data to improve our Platform, ensure security, and provide relevant content. Our legitimate interest does not override your fundamental rights and freedoms.</li>
                <li><strong className="text-[#E2E8F0]">Contractual Necessity (Article 6(1)(b)):</strong> When you engage with our Products, we process data necessary to deliver the services you have requested.</li>
              </ul>
            </Section>

            <Section title="5. How We Use Your Information">
              <p>We use your personal data for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>To respond to your demo requests and inquiries.</li>
                <li>To send you newsletter communications (only with your consent).</li>
                <li>To improve and optimize the Platform and user experience.</li>
                <li>To analyze usage patterns and trends for internal analytics.</li>
                <li>To ensure the security and proper functioning of the Platform.</li>
                <li>To comply with legal obligations.</li>
              </ul>
            </Section>

            <Section title="6. Data Sharing and Third Parties">
              <p>We do not sell your personal data to third parties. We may share your data with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-[#E2E8F0]">Service Providers:</strong> Trusted third-party providers who assist us in operating the Platform, including hosting providers, email services, and analytics tools. These providers are contractually obligated to protect your data and process it only on our instructions.</li>
                <li><strong className="text-[#E2E8F0]">Legal Requirements:</strong> When required by law, regulation, legal process, or governmental request.</li>
                <li><strong className="text-[#E2E8F0]">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your data may be transferred as part of the business transaction.</li>
              </ul>
            </Section>

            <Section title="7. International Data Transfers">
              <p>As Procureaide Pvt Ltd is registered in Pakistan, your data may be transferred to and processed in jurisdictions outside the European Economic Area (EEA). When such transfers occur, we ensure appropriate safeguards are in place, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Standard Contractual Clauses (SCCs) approved by the European Commission.</li>
                <li>Ensuring the receiving country provides an adequate level of data protection.</li>
                <li>Obtaining your explicit consent where required.</li>
              </ul>
            </Section>

            <Section title="8. Cookies">
              <p>Our Platform uses cookies and similar tracking technologies to enhance your browsing experience. Cookies are categorized as follows:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-[#E2E8F0]">Strictly Necessary Cookies:</strong> Essential for the Platform to function. These cannot be disabled.</li>
                <li><strong className="text-[#E2E8F0]">Analytics Cookies:</strong> Help us understand how visitors interact with the Platform. These are only set with your consent.</li>
                <li><strong className="text-[#E2E8F0]">Functional Cookies:</strong> Remember your preferences and settings to provide a personalized experience.</li>
              </ul>
              <p>You can manage your cookie preferences through your browser settings or via the cookie consent banner displayed on your first visit. Withdrawing consent for non-essential cookies will not affect the functionality of essential Platform features.</p>
            </Section>

            <Section title="9. Your Rights Under GDPR">
              <p>If you are in the EEA, UK, or any jurisdiction with similar data protection laws, you have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-[#E2E8F0]">Right of Access (Article 15):</strong> You have the right to request a copy of your personal data we hold.</li>
                <li><strong className="text-[#E2E8F0]">Right to Rectification (Article 16):</strong> You may request correction of inaccurate or incomplete personal data.</li>
                <li><strong className="text-[#E2E8F0]">Right to Erasure (Article 17):</strong> You may request deletion of your personal data ("right to be forgotten"), subject to legal obligations.</li>
                <li><strong className="text-[#E2E8F0]">Right to Restriction (Article 18):</strong> You may request restriction of processing of your personal data under certain conditions.</li>
                <li><strong className="text-[#E2E8F0]">Right to Data Portability (Article 20):</strong> You may request your data in a structured, commonly used, machine-readable format.</li>
                <li><strong className="text-[#E2E8F0]">Right to Object (Article 21):</strong> You may object to processing based on legitimate interests or direct marketing.</li>
                <li><strong className="text-[#E2E8F0]">Right to Withdraw Consent:</strong> Where processing is based on consent, you may withdraw consent at any time without affecting the lawfulness of processing based on consent before its withdrawal.</li>
              </ul>
              <p className="mt-3">To exercise any of these rights, please contact us at <a href="mailto:info@procureaide.com" className="text-[#00B4D8] hover:underline">info@procureaide.com</a>. We will respond to your request within 30 days.</p>
            </Section>

            <Section title="10. Data Retention">
              <p>We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-[#E2E8F0]">Demo Requests & Contact Forms:</strong> Retained for up to 24 months from submission, unless you request earlier deletion.</li>
                <li><strong className="text-[#E2E8F0]">Newsletter Subscriptions:</strong> Retained until you unsubscribe or request deletion.</li>
                <li><strong className="text-[#E2E8F0]">Usage and Analytics Data:</strong> Retained in anonymized form for up to 26 months.</li>
              </ul>
              <p>After the retention period, personal data is securely deleted or anonymized.</p>
            </Section>

            <Section title="11. Data Security">
              <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encryption of data in transit (TLS/SSL).</li>
                <li>Secure database storage with access controls.</li>
                <li>Regular security assessments and monitoring.</li>
                <li>Access limited to authorized personnel on a need-to-know basis.</li>
              </ul>
            </Section>

            <Section title="12. Children's Privacy">
              <p>The Platform is not intended for individuals under the age of 16. We do not knowingly collect personal data from children. If we become aware that we have collected data from a child under 16, we will take steps to delete that information promptly.</p>
            </Section>

            <Section title="13. Right to Lodge a Complaint">
              <p>If you believe that your data protection rights have been violated, you have the right to lodge a complaint with a supervisory authority. If you are in the EU, you can file a complaint with the data protection authority in your country of residence. For a list of EU data protection authorities, visit the European Data Protection Board website.</p>
            </Section>

            <Section title="14. Changes to This Policy">
              <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.</p>
            </Section>

            <Section title="15. Contact Us">
              <p>If you have any questions about this Privacy Policy, wish to exercise your data rights, or have concerns about how your data is being processed, please contact us:</p>
              <ul className="list-none space-y-1">
                <li><strong className="text-[#F8FAFC]">Procureaide Pvt Ltd</strong></li>
                <li>Email: <a href="mailto:info@procureaide.com" className="text-[#00B4D8] hover:underline">info@procureaide.com</a></li>
                <li>Website: <a href="https://procureaide.co" className="text-[#00B4D8] hover:underline">procureaide.co</a></li>
              </ul>
            </Section>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
